<template>
  <v-card>
    <v-card-title class="d-flex align-center">
      <v-icon class="mr-2">mdi-chart-timeline-variant</v-icon>
      Abandoned at Step vs Quotes Received — Monthly
    </v-card-title>
    <v-card-subtitle class="text-wrap">
      Stacked columns = abandonments by step · Line = total quotes received ·
      Dashed line = abandoned as % of quotes received (right axis)
    </v-card-subtitle>
    <v-card-text>
      <div v-if="loading" class="text-center py-8">
        <v-progress-circular indeterminate color="primary"></v-progress-circular>
      </div>
      <div v-else-if="series.length === 0" class="text-center py-8 text-medium-emphasis">
        No data available
      </div>
      <div v-else>
        <apexchart
          type="line"
          height="400"
          :options="chartOptions"
          :series="series"
        />
      </div>
    </v-card-text>
  </v-card>
</template>

<script>
import VueApexCharts from "vue3-apexcharts";
import { comebackService } from "../services";

// Same categorical palette as AbandonedStepsTimelineChart so the steps read
// consistently across both charts. The quotes line uses a distinct dark ink
// so it never gets confused with a step colour.
const STEP_COLORS = [
  "#1976D2", "#E91E63", "#4CAF50", "#FF9800", "#9C27B0",
  "#00BCD4", "#FF5722", "#3F51B5", "#8BC34A", "#F44336",
  "#009688", "#FFC107", "#673AB7", "#03A9F4", "#CDDC39",
];
const QUOTES_LINE_COLOR = "#263238";
// Derived metric on its own (right) axis, so it needs a colour that is clearly
// outside the step palette.
const RATE_LINE_COLOR = "#C62828";
const QUOTES_SERIES = "Quotes Received";
const RATE_SERIES = "Abandonment Rate";
// Series that are not abandoned-step columns — excluded from the stacked total.
const NON_STEP_SERIES = [QUOTES_SERIES, RATE_SERIES];

export default {
  name: "AbandonedVsQuotesTimelineChart",
  components: { apexchart: VueApexCharts },
  props: {
    startDate: { type: [String, Date], default: null },
    endDate: { type: [String, Date], default: null },
  },
  data() {
    return {
      loading: true,
      series: [],
      chartOptions: this.buildBaseOptions([]),
    };
  },
  methods: {
    buildBaseOptions(categories) {
      return {
        chart: {
          type: "line",
          stacked: true,
          toolbar: { show: true, tools: { download: true, selection: false, zoom: false, zoomin: false, zoomout: false, pan: false, reset: false } },
        },
        plotOptions: {
          bar: {
            columnWidth: "60%",
            borderRadius: 4,
            borderRadiusApplication: "end",
          },
        },
        xaxis: {
          categories,
          title: { text: "Month" },
        },
        yaxis: {
          title: { text: "Count" },
          decimalsInFloat: 0,
          labels: { formatter: (val) => Math.round(val) },
        },
        legend: { position: "top" },
        dataLabels: { enabled: false },
        tooltip: {
          shared: true,
          intersect: false,
          // Custom popup so we can add a "Total abandoned" row to compare
          // against Quotes Received, and show each step's share alongside it.
          custom: function ({ dataPointIndex, w }) {
            // Categories arrive as "YYYY-MM" — show the month name and year.
            // `categories` is closed over from buildBaseOptions, which is the
            // reliable source (ApexCharts' internal label arrays are not on
            // this mixed column+line chart).
            const rawMonth = categories[dataPointIndex];
            const monthNames = [
              "January", "February", "March", "April", "May", "June",
              "July", "August", "September", "October", "November", "December",
            ];
            const [yr, mo] = String(rawMonth).split("-");
            const moIdx = parseInt(mo, 10) - 1;
            const month =
              yr && moIdx >= 0 && moIdx <= 11
                ? `${monthNames[moIdx]} ${yr}`
                : rawMonth;
            const names = w.globals.seriesNames;
            const colors = w.globals.colors;
            const valAt = (i) => {
              const v = w.globals.series[i][dataPointIndex];
              return typeof v === "number" ? v : 0;
            };

            // Total abandoned = sum of the step columns (exclude the quotes
            // line and the derived rate line).
            let totalAbandoned = 0;
            names.forEach((n, i) => {
              if (!NON_STEP_SERIES.includes(n)) totalAbandoned += valAt(i);
            });

            const rows = names
              .map((n, i) => {
                let display;
                if (n === RATE_SERIES) {
                  // Already a percentage — of quotes received, not of the stack.
                  const raw = w.globals.series[i][dataPointIndex];
                  display =
                    typeof raw === "number" ? `${raw.toFixed(1)}%` : "n/a";
                } else {
                  const pct =
                    !NON_STEP_SERIES.includes(n) && totalAbandoned > 0
                      ? ` (${((valAt(i) / totalAbandoned) * 100).toFixed(1)}%)`
                      : "";
                  display = `${Math.round(valAt(i))}${pct}`;
                }
                return `
                  <div style="display:flex;align-items:center;padding:2px 0;">
                    <span style="width:10px;height:10px;border-radius:50%;background:${colors[i]};margin-right:8px;flex:0 0 auto;"></span>
                    <span style="margin-right:12px;">${n}:</span>
                    <span style="margin-left:auto;font-weight:600;">${display}</span>
                  </div>`;
              })
              .join("");

            return `
              <div style="padding:8px 12px;min-width:230px;font-size:12px;">
                <div style="font-weight:700;margin-bottom:6px;">${month}</div>
                ${rows}
                <div style="display:flex;align-items:center;margin-top:6px;padding-top:6px;border-top:1px solid rgba(0,0,0,0.12);">
                  <span style="margin-right:12px;font-weight:600;">Total abandoned:</span>
                  <span style="margin-left:auto;font-weight:700;">${Math.round(totalAbandoned)}</span>
                </div>
              </div>`;
          },
        },
      };
    },
    toYmd(d) {
      if (!d) return undefined;
      const dt = new Date(d);
      const m = String(dt.getMonth() + 1).padStart(2, "0");
      const day = String(dt.getDate()).padStart(2, "0");
      return `${dt.getFullYear()}-${m}-${day}`;
    },
    async loadData() {
      this.loading = true;
      try {
        const response = await comebackService.getAbandonedStepsVsQuotesTimeline({
          startDate: this.toYmd(this.startDate),
          endDate: this.toYmd(this.endDate),
        });
        if (response?.success && response.data) {
          const { months, series, quotesReceived } = response.data;

          // Abandoned steps as stacked columns, quotes received as an overlaid line.
          const stepSeries = series.map((s) => ({
            name: s.name,
            type: "column",
            data: s.data,
          }));
          const quotes = quotesReceived || [];
          const quotesSeries = {
            name: QUOTES_SERIES,
            type: "line",
            data: quotes,
          };

          // Abandonments as a share of the quotes received that month, so a
          // rising or falling trend is readable independently of volume.
          // Months with no quotes get null (a gap) rather than a false 0.
          const rateSeries = {
            name: RATE_SERIES,
            type: "line",
            data: months.map((_, i) => {
              const received = quotes[i] || 0;
              if (!received) return null;
              const abandoned = stepSeries.reduce(
                (sum, s) => sum + (s.data[i] || 0),
                0
              );
              return Number(((abandoned / received) * 100).toFixed(1));
            }),
          };

          this.series = [...stepSeries, quotesSeries, rateSeries];

          const stepCount = stepSeries.length;
          const rateIndex = this.series.length - 1;
          // Counts (steps + quotes) all share the left axis; the rate gets its
          // own percentage axis on the right.
          const countAxisName = stepSeries.length
            ? stepSeries[0].name
            : QUOTES_SERIES;

          this.chartOptions = {
            ...this.buildBaseOptions(months),
            // Per-series colours: one per step, the dark quotes line, then the
            // rate line.
            colors: [
              ...stepSeries.map((_, i) => STEP_COLORS[i % STEP_COLORS.length]),
              QUOTES_LINE_COLOR,
              RATE_LINE_COLOR,
            ],
            yaxis: [
              // One entry per count series, all pinned to the same scale.
              ...Array.from({ length: stepCount + 1 }, (_, i) => ({
                seriesName: countAxisName,
                show: i === 0,
                // Keep the shape Apex expects; only the first axis is labelled.
                title: { text: i === 0 ? "Count" : undefined },
                decimalsInFloat: 0,
                labels: { formatter: (val) => Math.round(val) },
              })),
              {
                seriesName: RATE_SERIES,
                opposite: true,
                min: 0,
                forceNiceScale: true,
                title: { text: "Abandoned / Quotes Received" },
                labels: {
                  formatter: (val) =>
                    typeof val === "number" ? `${val.toFixed(0)}%` : val,
                },
              },
            ],
            // No stroke on the columns; a 3px smooth line for quotes and a
            // dashed line for the derived rate.
            stroke: {
              width: [...Array(stepCount).fill(0), 3, 3],
              dashArray: [...Array(stepCount).fill(0), 0, 6],
              curve: "smooth",
            },
            // Markers only on the two lines.
            markers: {
              size: [...Array(stepCount).fill(0), 5, 4],
            },
            // Label the rate line directly — it is the metric to read at a
            // glance, and it lives on the axis people look at least.
            dataLabels: {
              enabled: true,
              enabledOnSeries: [rateIndex],
              formatter: (val) =>
                typeof val === "number" ? `${val.toFixed(1)}%` : "",
              style: { fontSize: "11px", fontWeight: 600 },
              background: {
                enabled: true,
                backgroundColor: RATE_LINE_COLOR,
                foreColor: "#fff",
                borderWidth: 0,
                borderRadius: 3,
                padding: 4,
                opacity: 0.95,
              },
              offsetY: -6,
            },
          };
        } else {
          this.series = [];
        }
      } catch (e) {
        console.error("Failed to load abandoned steps vs quotes timeline", e);
        this.series = [];
      } finally {
        this.loading = false;
      }
    },
  },
  computed: {
    // Single key for both bounds so moving the range triggers one reload.
    rangeKey() {
      return `${this.toYmd(this.startDate)}|${this.toYmd(this.endDate)}`;
    },
  },
  watch: {
    rangeKey() { this.loadData(); },
  },
  mounted() {
    this.loadData();
  },
};
</script>
